import React from "react";
import { isEmpty, filter } from "lodash";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card } from "react-bootstrap";
import { FiCheck } from "react-icons/fi";
import { getIraAccountIdSelector } from "store/selectors/user";
import { createLoadingSelector, createErrorSelector } from "store/selectors";
import { addContributionSource } from "actions/contributionActions";
import { makeIraWithdrawal } from "actions/withdrawalActions";
import { accountTypesToEnglishMapping } from "statics/accountTypes";
import {
  contributionConstants,
  withdrawalConstants,
  transactionConstants,
  accountConstants,
} from "actions/types";
import TransferDetails from "components/TransferDetails";
import IconMessageCard from "components/IconMessageCard";
import Button from "components/Button";
import Alert from "components/Alert";
import type { RootState } from "types/state";

/** Shape structurally compatible with TransferDetails' (non-exported) metadata prop. */
interface TransferTableProperty {
  key: string;
  label?: string;
  format?: string | ((data: unknown) => unknown);
}

/** The transfer form values passed in by the parent. propTypes only said `object`. */
interface ConfirmTransferValues {
  amount: number;
  isFullDisbursement?: boolean;
  reason?: string;
  [key: string]: unknown;
}

const accountType = (accountType: unknown) => {
  return (
    accountTypesToEnglishMapping[
      accountType as keyof typeof accountTypesToEnglishMapping
    ] || accountType
  );
};

const commonProperties: TransferTableProperty[] = [
  {
    key: "fromAccount.bankAlias",
    label: "FROM",
  },
  {
    key: "toAccount.accountType",
    label: "TO",
    format: accountType,
  },
  {
    key: "amount",
    format: "currency",
    label: "AMOUNT",
  },
];

const contributionTableProperties: TransferTableProperty[] = [
  ...commonProperties,
  {
    key: "transferDate",
    label: "TRANSFER DATE",
  },
  {
    key: "frequency",
    label: "FREQUENCY",
  },
  {
    key: "taxYear",
    label: "TAX YEAR",
  },
];

const iraWithdrawTableProperties: TransferTableProperty[] = [
  ...commonProperties,
  {
    key: "isFullDisbursement",
    format: "boolean",
    label: "FULL DISBURSEMENT",
  },
];

const actions = [
  contributionConstants.CONTRIBUTION,
  contributionConstants.GET_CONTRIBUTION_ABILITY,
  accountConstants.GET_IRA_BALANCE,
  transactionConstants.GET_TRANSACTIONS,
  withdrawalConstants.WITHDRAWAL,
];

const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => ({
  isFetching: loadingSelector(state),
  error: errorSelector(state),
  iraAccountId: getIraAccountIdSelector(state),
});

const mapDispatchToProps = {
  addContributionSource,
  makeIraWithdrawal,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

/** Props the parent passes (propTypes minus redux-injected props minus `client`). */
interface ConfirmTransferOwnProps {
  onClose: React.MouseEventHandler<HTMLButtonElement>;
  isWithdrawal?: boolean;
  show?: boolean;
  values: ConfirmTransferValues;
  bankId?: string;
  onSuccess?: () => void;
  isApplyToPriorYear?: boolean;
}

type ConfirmTransferProps = PropsFromRedux & ConfirmTransferOwnProps;

interface ConfirmTransferState {
  submittedContribution: boolean;
}

class ConfirmTransfer extends React.PureComponent<
  WithApolloClient<ConfirmTransferProps>,
  ConfirmTransferState
> {
  constructor(props: WithApolloClient<ConfirmTransferProps>) {
    super(props);

    this.state = {
      submittedContribution: false,
    };
  }

  _onSubmit = () => {
    if (!this.props.isWithdrawal) {
      const requestObj = {
        amount: +this.props.values.amount,
        iraAccountId: this.props.iraAccountId,
        applyToPriorYear: this.props.isApplyToPriorYear,
        bankId: this.props.bankId,
      };

      void this.props
        // pre-existing: iraAccountId (string | null | undefined) and bankId
        // (string | undefined) are passed unguarded into the action, which
        // requires non-null strings — preserve runtime behavior.
        .addContributionSource(
          this.props.client as ApolloClient<object>,
          // @ts-expect-error nullable redux/own props fed into strict action args
          requestObj
        )
        .then(() => {
          this.setState({ submittedContribution: true });
        });
    } else {
      // user is making a withdrawal request
      const { isFullDisbursement, reason } = this.props.values;
      let { amount } = this.props.values;
      amount = isFullDisbursement ? 0 : +amount;

      const requestObj = {
        amount,
        isFullDisbursement,
        reason,
        iraAccountId: this.props.iraAccountId,
      };
      void this.props
        // pre-existing: iraAccountId (nullable), isFullDisbursement/reason
        // (optional) passed unguarded into the strict action args.
        .makeIraWithdrawal(
          this.props.client as ApolloClient<object>,
          // @ts-expect-error nullable/optional values fed into strict action args
          requestObj
        )
        .then(() => {
          this.setState({ submittedContribution: true });
        });
    }
  };

  _successContent = () => {
    const header = "You’ve successfully initiated a transfer.";
    const subheader = "Funds should arrive in 5 business days.";
    const actionText = "Close";

    return (
      <>
        <IconMessageCard
          header={header}
          subheader={subheader}
          icon={<FiCheck color="white" stroke="#60A4BF" size={16} />}
          actionText={actionText}
          onClick={() => {
            // onSuccess is optional in props but invoked unconditionally here
            this.props.onSuccess!();
          }}
        />
      </>
    );
  };

  _infoContent = () => {
    let tablePropsToUse = this.props.isWithdrawal
      ? iraWithdrawTableProperties
      : contributionTableProperties;

    if (this.props.isWithdrawal && this.props.values.isFullDisbursement) {
      tablePropsToUse = filter(
        tablePropsToUse,
        (prop) => prop.key !== "amount"
      );
    }

    return (
      <Card className="transfer-card">
        <Card.Body>
          <TransferDetails
            data={this.props.values}
            metadata={tablePropsToUse}
            title="Review transfer details"
            showHeader={true}
            showAuthorization={true}
            headerConfig={{
              showTiming: true,
              timingText: "Funds should arrive in 5 business days.",
            }}
          />
          {this.props.error && <Alert type="error" msg={this.props.error} />}
          <section className="form-sec-2col">
            <div className="submit-row btn-row">
              <Button
                btnLabel="Cancel"
                color="cancel"
                name="cancel"
                onClick={this.props.onClose}
              />
              <Button
                withArrow
                name="submit"
                btnLabel="Submit Transfer"
                onClick={this._onSubmit}
                loading={this.props.isFetching}
              />
            </div>
          </section>
        </Card.Body>
      </Card>
    );
  };

  render() {
    const successfullyContributed =
      this.state.submittedContribution && isEmpty(this.props.error);
    const content = successfullyContributed
      ? this._successContent()
      : this._infoContent();

    return <>{content}</>;
  }
}

export default connector(withApollo<ConfirmTransferProps>(ConfirmTransfer));
